import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { findAssignedElement } from '@zywave/zui-base/dist/utils/find-assigned-element.js';
import { style } from './zui-notifier-css.js';
import '@zywave/zui-icons';
import '@zywave/zui-button';

/**
 * `<zui-notifier>` is used to inform users with information as a result of their action.
 *
 * @element zui-notifier
 *
 * @event open - Dispatched event when `<zui-notifier>` is opening / transitioning open
 * @event close - Dispatched event when `<zui-notifier>` is closed, after the closing transition
 *
 * @slot action - Notifiers can include a single action or link, when there is more information associated with the message that the user may choose to explore
 *
 * @cssprop [--zui-notifier-theme-color=var(--zui-blue)] - Strongly discourage changing this, so the theming is accurately reflecting the design specs
 * @cssprop [--zui-notifier-action-margin-top=0.375rem (6px)] - Ability to override notifier action's default `margin-top` value
 */
export class ZuiNotifier extends ZuiBaseElement {
  /**
   * Header text
   */
  @property({ type: String, attribute: 'header' }) header = '';

  /**
   * Message body text
   */
  @property({ type: String, attribute: 'message' }) message = '';

  /**
   * The `parent-selector` determines which element `<zui-notifier>` will be positionally based off of. The noitifier will then be visually pinned to the top and right corner of the element provided. If no value is given, the default will base positioning off of the `parentElement` of a `<zui-notifier>`. Provide a `string` value as you would for `document.querySelector('string')`.
   */
  @property({ type: String, attribute: 'parent-selector' }) parentSelector: string | null = null;

  /**
   * The type determines which `zui-icon` to display and which color theme to apply
   * @type {"info" | "success" | "error" | "warning"}
   */
  @property({ type: String, attribute: 'type', reflect: true }) type: ZuiNotifierType = 'info';

  /**
   * The amount of time, in milliseconds, this notifier should persist open. A duration set to `0` or `-1` will remain shown until closed.
   */
  @property({ type: Number, attribute: 'duration', reflect: true }) duration = 5000;

  @query('.notifier-container-outer')
  _notifierContainerEl: HTMLElement;

  @query('slot[name="action"]')
  _actionSlot: HTMLSlotElement;

  private get _slottedActionEl() {
    return findAssignedElement(this._actionSlot, '*');
  }

  _isHidden = true;

  _isClosing = false;

  static get styles() {
    return [super.styles, style];
  }

  /**
   * Computes which icon to show, based on notifier type.
   *
   * @param {string} type  This determines which zui-icon this notifier will use. ('info', 'success', 'error' or 'warning')
   */
  _computeIcon(type: string) {
    switch (type) {
      case 'error':
        return 'zui-error';
      case 'success':
        return 'zui-success';
      case 'warning':
        return 'zui-warning';
      case 'info':
      default:
        return 'zui-info';
    }
  }

  #durationCountdownTimeout: null | number = null;
  #slideInAnimationDuration: null | number = null;

  _resetTimer() {
    clearTimeout(this.#durationCountdownTimeout);
    this.#durationCountdownTimeout = window.setTimeout(() => this.hide(), this.duration);
  }

  private _onAnimationEnd(e: AnimationEvent) {
    // references keyframe animation in stylesheet
    if (e.animationName === 'notifier-appear') {
      this.#slideInAnimationDuration = e.elapsedTime * 1000;
    }
    // triggered by closing animation on .notifier-container-outer
    if (e.animationName === 'notifier-disappear') {
      this._isHidden = true;
      this._isClosing = false;
      this.requestUpdate();
      this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true, detail: this }));
    }
  }

  private _notifierCoordinates: ZuiNotifierPoseType | null = null;

  private _determineNotifierCoordinates(): ZuiNotifierPoseType {
    const parentEl =
      (this.getRootNode() as HTMLElement).querySelector(this.parentSelector as string) || this.parentElement;
    const parentRect = parentEl.getBoundingClientRect();
    const notifierPose = {
      top: `${parentRect.top}px`,
      right: `${window.innerWidth - parentRect.right}px`,
    };
    return notifierPose;
  }

  private _addNotifierStyleCoordinates() {
    this.style.top = this._notifierCoordinates.top;
    this.style.right = this._notifierCoordinates.right;
  }

  /**
   * Displays the notifier
   */
  async show(): Promise<void> {
    await this.updateComplete;
    this._notifierCoordinates = this._determineNotifierCoordinates();
    this._addNotifierStyleCoordinates();
    this._isHidden = false;
    this._isClosing = false;
    this.requestUpdate();
    this.duration >= 1 && setTimeout(() => this._resetTimer(), this.#slideInAnimationDuration);
    this.dispatchEvent(new CustomEvent('open', { bubbles: true, composed: true, detail: this }));
  }

  /**
   * Hides the notifier
   */
  hide(): void {
    clearTimeout(this.#durationCountdownTimeout);
    this._isClosing = true;
    this.requestUpdate();
  }

  firstUpdated() {
    this._slottedActionEl && this._slottedActionEl.addEventListener('click', () => this.hide());
  }

  render() {
    const icon = this._computeIcon(this.type);

    return html`
      <div
        class="notifier-container-outer  
        ${classMap({
          'is-closing': this._isClosing,
          'is-hidden': this._isHidden,
        })}"
        @animationend="${this._onAnimationEnd}"
      >
        <div class="notifier-bar"></div>
        <div class="notifier-content">
          <zui-button
            class="notifier-close-button link icon-only"
            @click="${this.hide}"
            tabindex="0"
            aria-label="close"
          >
            <zui-icon icon="zui-remove"></zui-icon>
          </zui-button>
          <div class="notifier-icon">
            <zui-icon icon="${icon}"></zui-icon>
          </div>
          <div class="notifier-text">
            <h3 class="notifier-header">${this.header}</h3>
            <div class="notifier-message">${this.message}</div>
            <slot name="action"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

window.customElements.define('zui-notifier', ZuiNotifier);

export type ZuiNotifierType = 'info' | 'success' | 'error' | 'warning';

export type ZuiNotifierTypeIcons = {
  info: 'zui-info';
  success: 'zui-success';
  error: 'zui-error';
  warning: 'zui-warning';
};

export type ZuiNotifierPoseType = {
  top: string;
  right: string;
};

declare global {
  interface HTMLElementTagNameMap {
    'zui-notifier': ZuiNotifier;
  }
}
